/**
 * Editorial copy for the public pricing page — the words a human edits, kept apart from the
 * numbers a machine owns. Prices, price ids, and what's included come from the server
 * (`GET /api/paddle-config`, read out of the plans table); this file only carries the pitch:
 * the one-line tagline, the feature bullets, and which tier is highlighted.
 *
 * Each entry is matched to a plan by `code` (== plans.code). A tier the server returns with no
 * copy here still renders — with its plan name and an empty pitch — so a new plan is never
 * hidden by a forgotten edit; add its copy here to give it a voice. Deliberately NO dollar
 * amounts in this file: the headline price is whatever Paddle returns for the viewer's country,
 * and duplicating it here would let the two drift.
 */
export interface TierCopy {
  /** Matches plans.code from the server. */
  code: string;
  /** One line under the plan name. */
  tagline: string;
  /** Short "what you get" bullets, in display order. */
  features: string[];
  /** The visually promoted column ("Most popular"). At most one should be true. */
  highlight?: boolean;
  /** Overrides the default "Subscribe" button label when set. */
  cta?: string;
}

export const TIER_COPY: TierCopy[] = [
  {
    code: 'nightshift',
    tagline: 'After-hours coverage for a single line.',
    features: [
      'Answers every call after hours',
      'Pages your on-call tech by phone',
      'Voicemail and a written summary by morning',
      'One phone number, one brand',
    ],
  },
  {
    code: 'roundtheclock',
    tagline: 'For busier lines and a real on-call rotation.',
    highlight: true,
    features: [
      'Everything in Night Shift',
      'Room for heavy nights and storm surges',
      'Up to three phone numbers',
      'A lower per-minute rate past the plan',
    ],
  },
  {
    code: 'switchboard',
    tagline: 'For multi-brand operators and MSPs.',
    features: [
      'Everything in Round-the-Clock',
      'Up to ten brands on one account',
      'Ten phone numbers',
      'Our lowest per-minute rate',
    ],
  },
];

export function copyFor(code: string): TierCopy | undefined {
  return TIER_COPY.find((t) => t.code === code);
}
